import { http } from '@/lib/axios';
import { toApiError } from '@/lib/unwrap';

/**
 * The park-wide booking window for individual tickets (#1488): visit dates
 * from `minDate` through `maxDate` (Asia/Jakarta civil days, `YYYY-MM-DD`,
 * inclusive). The sale endpoint refuses anything outside it with
 * 400 VISIT_DATE_OUT_OF_RANGE, so the picker bounds itself by the same range.
 */
export interface BookingWindow {
  presaleWindowDays: number;
  minDate: string;
  maxDate: string;
}

/** Matches the backend default (`business.presaleWindowDays`). */
export const DEFAULT_PRESALE_WINDOW_DAYS = 7;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Defensive extractor — the feed answers with the standard envelope
 * (`{ status, data: { presaleWindowDays, minDate, maxDate } }`) but a bare
 * object is tolerated too. Anything malformed is null.
 */
export function extractBookingWindow(payload: unknown): BookingWindow | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as { data?: unknown };
  const raw = (p.data && typeof p.data === 'object' ? p.data : payload) as Partial<BookingWindow>;
  if (
    !Number.isInteger(raw.presaleWindowDays) ||
    typeof raw.minDate !== 'string' ||
    typeof raw.maxDate !== 'string' ||
    !ISO_DAY.test(raw.minDate) ||
    !ISO_DAY.test(raw.maxDate)
  ) {
    return null;
  }
  return { presaleWindowDays: raw.presaleWindowDays as number, minDate: raw.minDate, maxDate: raw.maxDate };
}

/** GET /capacity/booking-window/public — no auth. */
export async function getBookingWindow(): Promise<BookingWindow | null> {
  try {
    const res = await http.get('/capacity/booking-window/public');
    return extractBookingWindow(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
